/* Shared */
import { WithContext } from '@auviknetworks/iceberg-ui';
import { KebabMenu } from 'application/Shared/Components/Menu/KebabMenu';
import {
    ConditionalTenantAppProvider,
    TenantAppContext,
    useTenantAppContext
} from 'application/Shared/Contexts/TenantAppContext';
import { Notify, SessionHelper } from 'application/Shared/Helpers';
import { Application } from 'application/Shared/types/application';
import { useSession } from 'hooks/redux/Selectors/session';

/* React */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import {
    SaasOpsApplicationContextProvider,
    useSaasOpsApplicationContext
} from '../../Application/SaasOpsApplicationContext';
import { WorkatoConnectionsModal } from '../WorkatoConnectionsModal';
import { getWorkatoStatus } from '../functions';
import { WorkatoStatus } from '../types';

const enum ActionTypes {
    offboard = 'offboard',
    saasOpsConfigure = 'saasOpsConfigure'
}

/**
 * A component that renders a kebab menu with SaasOps related actions. If options are sent in, a kebab menu with workato actions
 * will be appended to the existing options.
 * @param {Object} props - The component props
 * @param {Application} props.application - The application object
 * @param {string} props.dataTrackId - ID used for tracking menu interactions
 * @param {(value: string) => void} [props.onClickFn] - Optional callback for menu item clicks
 * @param {() => void} [props.onSuccessFn] - Function to run on success
 * @param {Array<object>} [props.options=[]] - Additional custom menu options. The Workato actions will be appended to the existing options.
 * @param {string} [props.userId] - Optional user ID
 * @returns {JSX.Element} The rendered kebab menu component
 */
const SaasOpsKebab = ({
    application,
    dataTrackId,
    onClickFn,
    onSuccessFn,
    options: customOptions = [],
    userId,
    userEmail
}: {
    application: Application;
    dataTrackId: string;
    onClickFn?: (value: string) => void;
    onSuccessFn?: () => void;
    options?: Array<object>;
    userId?: string;
    userEmail?: string;
}): JSX.Element => {
    const { t } = useTranslation();
    const session = useSession();
    const { clientId } = useParams();

    const [openSaasOpsConfigurationModal, setOpenSaasOpsConfigurationModal] =
        useState(false);

    const { workatoConnection, isOffboardAvailable, baseAdminRoute } =
        useSaasOpsApplicationContext();
    const { tenantApp, refetchTenantApps } = useTenantAppContext();

    const workatoProvider = application?.workatoProvider;
    const name = tenantApp?.name || application.name;
    const workatoStatus = getWorkatoStatus(workatoConnection);
    const saasOpsConfigurationRequired =
        workatoStatus !== WorkatoStatus.connected;
    const sessionHelper = new SessionHelper(session);

    const params: Record<string, string> = {};
    if (application) params.applicationId = application.id;
    if (userId) params.userId = userId;
    if (userEmail) params.email = userEmail;
    const searchParams = new URLSearchParams(params);

    const offboardUrl = `${baseAdminRoute}/saasOps/offboard?${searchParams.toString()}`;

    const workatoOptions = sessionHelper.hasPermission(
        ['system_admin', 'client_admin'],
        'or'
    )
        ? [
              {
                  isParent: true,
                  key: 'SaasOps.title',
                  label: t('SaasOps.title'),
                  value: 'saasOps'
              },
              {
                  dataTrackId: `saasOps_${dataTrackId}_actions_offboard`,
                  disabled: saasOpsConfigurationRequired,
                  key: 'Common.offboard',
                  label: `${t('Common.offboard')}`,
                  link: {
                      'data-testid': 'offboardLink',
                      style: {
                          color: 'inherit'
                      },
                      to: offboardUrl
                  },
                  value: ActionTypes.offboard
              },
              {
                  dataTrackId: `saasOps_${dataTrackId}_actions_configure`,
                  hide: !saasOpsConfigurationRequired,
                  key: 'Common.configure',
                  label: t('Common.configure'),
                  value: ActionTypes.saasOpsConfigure
              }
          ]
        : [];

    const options = [...customOptions];
    if (isOffboardAvailable) {
        options.push(...workatoOptions);
    }

    const handleClick = (value: string) => {
        if (value === ActionTypes.saasOpsConfigure) {
            setOpenSaasOpsConfigurationModal(true);
        } else if (onClickFn) {
            onClickFn(value);
        }
    };

    const kebabMenuTestId = userId
        ? `kebabMenu_actions_${userId}_${application.id}`
        : undefined;

    return (
        <>
            {options.length > 0 && (
                <KebabMenu
                    data-testid={kebabMenuTestId}
                    options={options}
                    onClick={handleClick}
                    paperProps={{ sx: { width: 240 } }}
                    defaultSelection={undefined}
                />
            )}
            {openSaasOpsConfigurationModal && (
                <WorkatoConnectionsModal
                    clientId={clientId}
                    workatoProvider={workatoProvider}
                    displayName={name}
                    setOpenModal={setOpenSaasOpsConfigurationModal}
                    onSuccessFn={() => {
                        setOpenSaasOpsConfigurationModal(false);
                        if (onSuccessFn) {
                            onSuccessFn();
                        }
                        refetchTenantApps();

                        Notify.s(
                            t('SaasOps.integration.connection.success', {
                                appName: name
                            })
                        );
                    }}
                />
            )}
        </>
    );
};

/**
 * A component that renders a SaasOps kebab menu with contextual wrapping based on tenant app presence.
 * If options are sent in, a kebab menu with workato actions will be appended to the existing options.
 * @param {Object} props - The component props
 * @param {Application} props.application - The application object

 * @param {string} props.dataTrackId - ID used for tracking menu interactions
 * @param {(value: string) => void} [props.onClickFn] - Optional callback for menu item clicks
 * @param {() => void} [props.onSuccessFn] - Function to call on success
 * @param {Array<object>} [props.options=[]] - Additional custom menu options. The Workato actions will be appended to these options.
 * @param {string} [props.userId] - Optional user ID
 * @returns {JSX.Element} The rendered kebab menu component with appropriate context wrapper
 */
export const SaasOpsKebabMenu = ({
    application,
    dataTrackId,
    onClickFn,
    onSuccessFn,
    options: customOptions = [],
    userId,
    userEmail
}: {
    application: Application;
    dataTrackId: string;
    onClickFn?: (value: string) => void;
    onSuccessFn?: () => void;
    options?: Array<object>;
    userId?: string;
    userEmail?: string;
}): JSX.Element => (
    <ConditionalTenantAppProvider applicationId={application.id}>
        <WithContext context={TenantAppContext}>
            {({ loading, tenantApp }) =>
                loading && !tenantApp ? null : (
                    <SaasOpsApplicationContextProvider tenantApp={tenantApp!}>
                        <SaasOpsKebab
                            application={application}
                            dataTrackId={dataTrackId}
                            onClickFn={onClickFn}
                            onSuccessFn={onSuccessFn}
                            options={customOptions}
                            userId={userId}
                            userEmail={userEmail}
                        />
                    </SaasOpsApplicationContextProvider>
                )
            }
        </WithContext>
    </ConditionalTenantAppProvider>
);
